import type { ComponentProps, Ref } from 'react';
import { SearchInput as TessraSearchInput } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

/*
 * `Omit` applied member by member. Tessra's props end in a union (exactly one of `aria-label` or
 * `aria-labelledby`), and the built-in `Omit` maps over the whole union at once, merging it into
 * one object where both names are optional, so a nameless field would compile again.
 */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

type TessraSearchInputProps = ComponentProps<typeof TessraSearchInput>;

/* `ref` is omitted because the wrapper sets its own from `inputRef`, after the spread. */
type SearchInputProps = DistributiveOmit<TessraSearchInputProps, 'onValueChange' | 'ref'> & {
  onChange: TessraSearchInputProps['onValueChange'];
  inputRef?: Ref<HTMLInputElement>;
};

/*
 * Tessra's `className` lands on the field's outer box, so the fade reaches the input through it.
 * A pure opacity transition the OS reduce-motion setting governs; the design system has no opinion
 * on it, so it stays ours.
 */
const PLACEHOLDER_FADE =
  '[&_input]:placeholder:transition-opacity [&_input]:placeholder:duration-base [&_input:focus]:placeholder:opacity-0';

/**
 * Canonical search field: Tessra's `SearchInput` with a translated placeholder and clear label.
 *
 * Leave focus to Tessra. A focus treatment added here stacks on its ring and turns the 1px
 * hairline into a heavy dark band.
 */
export function SearchInput({
  onChange,
  placeholder,
  clearLabel,
  inputRef,
  className,
  ...props
}: SearchInputProps) {
  const { t } = useTranslation('common');

  return (
    <TessraSearchInput
      {...props}
      ref={inputRef}
      onValueChange={onChange}
      placeholder={placeholder ?? t('ui.searchInput.search')}
      clearLabel={clearLabel ?? t('ui.searchInput.clearSearch')}
      className={clsx(PLACEHOLDER_FADE, className)}
    />
  );
}

SearchInput.displayName = 'SearchInput';
